import {
    ICitiesResponse,
    initialMyWishesState,
    initialWishDetailsState,
    initialWishesState,
    initialWishRulebookState,
    IWish,
    IWishesResponse,
    IWishImage,
    IWishRulebook,
    IWishState,
    WishStatus
} from "./Helper";
import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {SliceNames} from "../constants";
import {
    createWish,
    deleteWish,
    duplicateWish,
    editWish,
    getCitiesWithCounts,
    getPastWinners,
    getProfileWishes,
    getWishDetails,
    getWishes,
    getWishImages,
    getWishRulebook,
    publishWish,
    redrawWinner,
    terminateWish,
    updateRulebook,
    uploadImages
} from "./ActionCreators";

const initialValue: IWishState = {
    items: initialWishesState,
    fetchWishesLoading: false,
    details: initialWishDetailsState,
    detailsLoading: false,
    imagesLoading: false,
    images: [],
    actionLoading: false,
    pastWinners: [],
    activeProfileWishes: initialMyWishesState,
    finishedProfileWishes: initialMyWishesState,
    profileWishesLoading: false,
    selectedRulebook: initialWishRulebookState,
    rulebookLoading: false,
    citiesWithCounts: [],
    citiesWithCountsLoading: false,
    lastWishUpdateChange: new Date().toISOString()
}

const wishSlice = createSlice({
    name: SliceNames.WISH,
    initialState: initialValue,
    reducers: {
        reloadWishAfterAction: (state, action: PayloadAction<IWish>) => {
            state.details = (action.payload.id === state.details.id) ?
                action.payload
                : state.details
            state.items = {
                ...state.items,
                data: [...state.items.data]
                    .filter((w) => {
                        if (action.payload.id === w.id && action.payload.status === WishStatus.FINISHED) {
                            return false
                        }
                        return true
                    })
                    .map((w) => {
                        if (action.payload.id === w.id) return {...action.payload, status: w.status}
                        return w
                    }),
                count: action.payload.status === WishStatus.FINISHED ? state.items.count - 1 : state.items.count
            }
        },
        removeWish: (state, action: PayloadAction<string>) => {
            state.items = {
                ...state.items,
                count: state.items.count - 1,
                data: [...state.items.data].filter((w) => w.id !== action.payload)
            }
            state.activeProfileWishes = {
                ...state.activeProfileWishes,
                count: state.activeProfileWishes.count - 1,
                data: [...state.activeProfileWishes.data].filter((w) => w.id !== action.payload)
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(
            getProfileWishes.pending, (state) => {
                state.profileWishesLoading = true
            })
            .addCase(
                getProfileWishes.fulfilled, (state, action: PayloadAction<any>) => {
                    state[`${action.payload.status as "active" | "finished"}ProfileWishes`] = action.payload.data
                    state.profileWishesLoading = false
                })
            .addCase(
                getProfileWishes.rejected, (state) => {
                    state.profileWishesLoading = false
                })
            .addCase(
                getWishes.pending, (state) => {
                    state.fetchWishesLoading = true
                })
            .addCase(
                getWishes.fulfilled, (state, action: PayloadAction<IWishesResponse>) => {
                    state.items = action.payload
                    state.fetchWishesLoading = false
                })
            .addCase(
                getWishes.rejected, (state) => {
                    state.fetchWishesLoading = false
                })
            .addCase(
                getPastWinners.fulfilled, (state, action: PayloadAction<IWish[]>) => {
                    state.pastWinners = action.payload
                })
            .addCase(
                getWishDetails.pending, (state) => {
                    state.detailsLoading = true
                })
            .addCase(
                getWishDetails.fulfilled, (state, action: PayloadAction<IWish>) => {
                    state.details = action.payload
                    state.detailsLoading = false
                })
            .addCase(
                getWishDetails.rejected, (state) => {
                    state.detailsLoading = false
                })
            .addCase(
                getWishImages.pending, (state) => {
                    state.imagesLoading = true
                })
            .addCase(
                getWishImages.fulfilled, (state, action: PayloadAction<IWishImage[]>) => {
                    state.images = action.payload
                    state.imagesLoading = false
                })
            .addCase(
                getWishImages.rejected, (state) => {
                    state.imagesLoading = false
                })
            .addCase(
                createWish.pending, (state) => {
                    state.actionLoading = true
                })
            .addCase(
                createWish.fulfilled, (state) => {
                    state.actionLoading = false
                    state.lastWishUpdateChange = new Date().toISOString()
                })
            .addCase(
                createWish.rejected, (state) => {
                    state.actionLoading = false
                })
            .addCase(
                deleteWish.pending, (state) => {
                    state.actionLoading = true
                })
            .addCase(
                deleteWish.fulfilled, (state) => {
                    state.lastWishUpdateChange = new Date().toISOString()
                    state.actionLoading = false
                })
            .addCase(
                deleteWish.rejected, (state) => {
                    state.actionLoading = false
                })
            .addCase(
                publishWish.pending, (state) => {
                    state.actionLoading = true
                })
            .addCase(
                publishWish.fulfilled, (state) => {
                    state.lastWishUpdateChange = new Date().toISOString()
                    state.actionLoading = false
                })
            .addCase(
                publishWish.rejected, (state) => {
                    state.actionLoading = false
                })
            .addCase(
                terminateWish.pending, (state) => {
                    state.actionLoading = true
                })
            .addCase(
                terminateWish.fulfilled, (state) => {
                    state.lastWishUpdateChange = new Date().toISOString()
                    state.actionLoading = false
                })
            .addCase(
                terminateWish.rejected, (state) => {
                    state.actionLoading = false
                })
            .addCase(
                editWish.pending, (state) => {
                    state.actionLoading = true
                })
            .addCase(
                editWish.fulfilled, (state, action: PayloadAction<IWish>) => {
                    state.items = {
                        ...state.items,
                        data: state.items.data.map(w => {
                            if (w.id === action.payload.id) {
                                return action.payload
                            }
                            return w
                        })
                    }
                    state.actionLoading = false
                })
            .addCase(
                editWish.rejected, (state) => {
                    state.actionLoading = false
                })
            .addCase(
                uploadImages.pending, (state) => {
                    state.actionLoading = true
                })
            .addCase(
                uploadImages.fulfilled, (state) => {

                    state.actionLoading = false
                })
            .addCase(
                uploadImages.rejected, (state) => {
                    state.actionLoading = false
                })
            .addCase(
                getWishRulebook.pending, (state) => {
                    state.rulebookLoading = true
                })
            .addCase(
                getWishRulebook.fulfilled, (state, action: PayloadAction<IWishRulebook>) => {
                    state.selectedRulebook = action.payload
                    state.rulebookLoading = false
                })
            .addCase(
                getWishRulebook.rejected, (state) => {
                    state.selectedRulebook = initialWishRulebookState
                    state.rulebookLoading = false
                })
            .addCase(
                updateRulebook.pending, (state) => {
                    state.actionLoading = true
                })
            .addCase(
                updateRulebook.fulfilled, (state, action: PayloadAction<IWishRulebook>) => {
                    state.selectedRulebook = action.payload
                    state.actionLoading = false
                })
            .addCase(
                updateRulebook.rejected, (state) => {
                    state.actionLoading = false
                })
            .addCase(
                redrawWinner.pending, (state) => {
                    state.actionLoading = true
                })
            .addCase(
                redrawWinner.fulfilled, (state) => {
                    state.actionLoading = false
                })
            .addCase(
                redrawWinner.rejected, (state) => {
                    state.actionLoading = false
                })
            .addCase(
                duplicateWish.pending, (state) => {
                    state.actionLoading = true
                })
            .addCase(
                duplicateWish.fulfilled, (state) => {
                    state.lastWishUpdateChange = new Date().toISOString()
                    state.actionLoading = false
                })
            .addCase(
                duplicateWish.rejected, (state) => {
                    state.actionLoading = false
                })
            .addCase(
                getCitiesWithCounts.pending, (state) => {
                    state.citiesWithCountsLoading = true
                })
            .addCase(
                getCitiesWithCounts.fulfilled, (state, action: PayloadAction<ICitiesResponse[]>) => {
                    state.citiesWithCounts = action.payload
                    state.citiesWithCountsLoading = false
                })
            .addCase(
                getCitiesWithCounts.rejected, (state) => {
                    state.citiesWithCountsLoading = false
                })
    }
})

export default wishSlice.reducer

export const {reloadWishAfterAction, removeWish} = wishSlice.actions
